import cloneDeep from "lodash.clonedeep";

interface Item {
  id: number;
}

/**
 * Add an item to an array only if it does not already exist
 *
 * @param item
 * @param array
 * @returns
 */
export const addArrayItem = <T extends Item>(item: T, array: T[]): T[] => {
  const itemExists = !!array.find((arrayItem) => arrayItem.id === item.id);

  if (itemExists) {
    return array;
  }

  return [...cloneDeep(array), item];
};

/**
 * Add items to an existing array
 *
 * @param items
 * @param array
 * @returns
 */
export const addArrayItems = <T extends Item>(items: T[], array: T[]): T[] => {
  const newItems = items.filter((item) => {
    const itemExists = !!array.find((arrayItem) => arrayItem.id === item.id);

    return !itemExists;
  });

  return [...cloneDeep(array), ...newItems];
};

/**
 * Update item in an array
 *
 * @param newItem
 * @param array
 * @returns
 */
export const updateArrayItem = <T extends Item>(newItem: T, array: T[]): T[] => {
  const itemIndex = array.findIndex((item) => item.id === newItem.id);

  if (itemIndex !== -1) {
    const newArray = cloneDeep(array);
    newArray[itemIndex] = newItem;

    return newArray;
  }

  return array;
};

/**
 * Update items in an array
 *
 * @param updatedItems
 * @param array
 * @returns
 */
export const updateArrayItems = <T extends Item>(updatedItems: T[], array: T[]): T[] => {
  const clonedArray = cloneDeep(array);

  updatedItems.forEach((item) => {
    const indexOfOldItem = array.findIndex((oldItem) => item.id === oldItem.id);
    clonedArray[indexOfOldItem] = cloneDeep(item);
  });

  return clonedArray;
};

/**
 * Delete item from an array
 *
 * @param itemId
 * @param array
 * @returns
 */
export const removeArrayItem = <T extends Item>(itemId: number, array: T[]): T[] => {
  const clonedArray = cloneDeep(array);
  return clonedArray.filter((item) => item.id !== itemId);
};
